import * as XLSX from "xlsx";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    CloseCircleFilled,
    DatabaseOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InfoCircleOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Statistic,
    Steps,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;

// ─── Labels ────────────────────────────────────────────────────────────────

const FORMAT_LABELS = {
    EFORCE:           { label: "eFORCE", color: "blue",   desc: ".xls · toutes compagnies" },
    BSD:              { label: "BSD SANLAM", color: "orange", desc: ".xlsx · Sinistres Déclarés" },
    TEMPLATE:         { label: "Template FIDELIA", color: "green", desc: ".xlsx · format standard" },
    EFORCE_DECLARES:  { label: "eFORCE", color: "blue",   desc: ".xls · toutes compagnies" },
};

const NATURE_LABELS = { CORPOREL: "Corporel", MATERIEL: "Matériel", MIXTE: "Mixte" };
const NATURE_COLORS = { CORPOREL: "red", MATERIEL: "orange", MIXTE: "purple" };
const SORT_LABELS   = { A: "Anc. en instance", I: "En instance", O: "Ouvert", T: "Terminé", S: "Sans suite" };
const SORT_COLORS   = { A: "default", I: "blue", O: "green", T: "geekblue", S: "gray" };

// ─── Helpers ───────────────────────────────────────────────────────────────

const formatMontant = (v) =>
    v == null ? "—" : new Intl.NumberFormat("fr-FR").format(Math.round(v)) + " FCFA";

const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? String(iso) : d.toLocaleDateString("fr-FR");
};

// ─── Colonnes preview déclarations ─────────────────────────────────────────

const previewDeclColumns = [
    {
        title: "", key: "s", width: 36, fixed: "left",
        render: (_, r) => {
            if (r._dejaImporte)
                return <Tooltip title="Déjà présent en base — ignoré à l'import"><DatabaseOutlined style={{ color: "#9333ea" }} /></Tooltip>;
            if (r._avertissement)
                return <Tooltip title={r._avertissement}><WarningOutlined style={{ color: "#f97316" }} /></Tooltip>;
            if (r._info)
                return <Tooltip title={r._info}><InfoCircleOutlined style={{ color: "#1d4ed8" }} /></Tooltip>;
            return <CheckCircleOutlined style={{ color: "#1a6b3c" }} />;
        },
    },
    { title: "Ligne", dataIndex: "ligne", key: "ligne", width: 60,
      render: v => <Text type="secondary" style={{ fontSize: 11 }}>L{v ?? "?"}</Text> },
    { title: "N° Sinistre", dataIndex: "sinistre", key: "sinistre", width: 150, fixed: "left",
      render: v => <Text code style={{ fontSize: 11, fontWeight: 700 }}>{v || "—"}</Text> },
    { title: "N° Police", dataIndex: "police", key: "police", width: 130,
      render: v => <Text code style={{ fontSize: 11 }}>{v || "—"}</Text> },
    { title: "Immatriculation", dataIndex: "immat", key: "immat", width: 130,
      render: v => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text> },
    { title: "Survenance", dataIndex: "date", key: "date", width: 110,
      render: v => v ? <Text style={{ fontSize: 11 }}>{formatDate(v)}</Text>
                     : <Tag color="red" style={{ fontSize: 10 }}>Manquant</Tag> },
    { title: "Nature", dataIndex: "nature", key: "nature", width: 90,
      render: v => v ? <Tag color={NATURE_COLORS[v] || "default"} style={{ fontSize: 10 }}>
                           {NATURE_LABELS[v] || v}
                       </Tag> : <Text type="secondary">—</Text> },
    { title: "Évaluation", dataIndex: "evaluation", key: "evaluation", width: 130, align: "right",
      render: v => <Text style={{ fontSize: 12, color: "#166534" }}>
                       {v != null ? new Intl.NumberFormat("fr-FR").format(Math.round(v)) : "—"}
                   </Text> },
    { title: "Assuré", dataIndex: "assure", key: "assure", ellipsis: true, width: 150,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Sort", dataIndex: "sort", key: "sort", width: 100,
      render: v => v ? <Tag color={SORT_COLORS[v] || "default"} style={{ fontSize: 10 }}>
                           {SORT_LABELS[v] || v}
                       </Tag> : <Text type="secondary">—</Text> },
    { title: "Compagnie", dataIndex: "compagnie", key: "compagnie", width: 100,
      render: v => v ? <Tag color="geekblue" style={{ fontSize: 10 }}>{v}</Tag> : <Text type="secondary">—</Text> },
    { title: "Lieu", dataIndex: "lieu", key: "lieu", ellipsis: true, width: 130,
      render: v => <Text style={{ fontSize: 11 }}>{v || "—"}</Text> },
];

// ─── Colonnes erreurs rapport ───────────────────────────────────────────────

const erreurColumns = [
    { title: "Ligne",     dataIndex: "ligne",          key: "ligne",          width: 60,
      render: v => <Text type="secondary">{v ?? "—"}</Text> },
    { title: "N° Sinistre", dataIndex: "sinistre",     key: "sinistre",       width: 150,
      render: v => <Text code style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "N° Police",   dataIndex: "police",       key: "police",         width: 130,
      render: v => <Text code style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Immat.",      dataIndex: "immatriculation", key: "immat",        width: 120,
      render: v => v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text> },
    { title: "Compagnie",   dataIndex: "compagnie",    key: "compagnie",      width: 100,
      render: v => v ? <Tag color="geekblue" style={{ fontSize: 10 }}>{v}</Tag> : <Text type="secondary">—</Text> },
    { title: "Assuré",      dataIndex: "assure",       key: "assure",         width: 140, ellipsis: true,
      render: v => <Text style={{ fontSize: 12 }}>{v || "—"}</Text> },
    { title: "Cause du rejet", dataIndex: "erreur",   key: "erreur",
      render: v => <Text type="danger" style={{ fontSize: 12 }}>{v || "—"}</Text> },
];

// ─── Carte de prévisualisation ──────────────────────────────────────────────

const PreviewCard = ({ preview }) => {
    if (!preview.ok) {
        return (
            <Alert type="error" showIcon
                message={`Erreur — ${preview.fichier}`}
                description={preview.messageErreur}
                style={{ marginBottom: 12 }}
            />
        );
    }

    const fmt          = FORMAT_LABELS[preview.formatDetecte] || {};
    const repNature    = preview.repartitionNature    || {};
    const repSort      = preview.repartitionSort      || {};
    const repCompagnie = preview.repartitionCompagnie || {};

    return (
        <Card size="small" style={{ marginBottom: 16, borderLeft: "4px solid #242166" }}
            title={
                <Space wrap>
                    <FileExcelOutlined style={{ color: "#217346" }} />
                    <Text strong>{preview.fichier}</Text>
                    {fmt.label && <Tag color={fmt.color}>{fmt.label}</Tag>}
                    {fmt.desc  && <Text type="secondary" style={{ fontSize: 11 }}>{fmt.desc}</Text>}
                    <Tag color="default">{(preview.dureeParsingMs / 1000).toFixed(2)}s</Tag>
                </Space>
            }
        >
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Statistic title="Sinistres parsés" value={preview.totalSinistres}
                        valueStyle={{ color: "#242166", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Polices trouvées" value={preview.totalLiesTrouves}
                        valueStyle={{ color: "#52c41a", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Polices manquantes" value={preview.totalLiesManquants}
                        valueStyle={{ color: preview.totalLiesManquants > 0 ? "#ff4d4f" : "#52c41a", fontSize: 22 }} />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic title="Période"
                        formatter={() => (
                            <Text style={{ fontSize: 12 }}>
                                {formatDate(preview.dateSurvenanceMin)} → {formatDate(preview.dateSurvenanceMax)}
                            </Text>
                        )}
                    />
                </Col>
            </Row>

            {preview.totalDejaImportes > 0 && (
                <Alert
                    type="info" showIcon
                    icon={<DatabaseOutlined style={{ color: "#9333ea" }} />}
                    style={{ marginBottom: 12 }}
                    message={
                        <span>
                            <Text strong style={{ color: "#7e22ce" }}>{preview.totalDejaImportes}</Text>
                            {" "}sinistre(s) déjà en base — ignoré(s) à l'import (badge violet sur les lignes).
                        </span>
                    }
                />
            )}

            <Divider style={{ margin: "10px 0" }} />

            <Row gutter={16}>
                <Col xs={24} md={7}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                        Répartition par nature
                    </Text>
                    <Space wrap>
                        {Object.entries(repNature).map(([k, v]) => (
                            <Tag key={k} color={NATURE_COLORS[k] || "default"}>
                                {NATURE_LABELS[k] || k} : <strong>{v}</strong>
                            </Tag>
                        ))}
                        {Object.keys(repNature).length === 0 && <Text type="secondary">—</Text>}
                    </Space>
                </Col>
                <Col xs={24} md={7}>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                        Répartition par état dossier
                    </Text>
                    <Space wrap>
                        {Object.entries(repSort).map(([k, v]) => (
                            <Tag key={k} color={SORT_COLORS[k] || "default"}>
                                {SORT_LABELS[k] || k} : <strong>{v}</strong>
                            </Tag>
                        ))}
                        {Object.keys(repSort).length === 0 && <Text type="secondary">—</Text>}
                    </Space>
                </Col>
                <Col xs={24} md={10}>
                    <Descriptions size="small" column={1} bordered={false}>
                        <Descriptions.Item label="Somme évaluations">
                            <Text strong style={{ color: "#166534" }}>
                                {formatMontant(preview.sommeEvaluations)}
                            </Text>
                        </Descriptions.Item>
                        {Object.keys(repCompagnie).length > 0 && (
                            <Descriptions.Item label="Compagnies">
                                <Space wrap size={4}>
                                    {Object.entries(repCompagnie).map(([k, v]) => (
                                        <Tag key={k} color="geekblue" style={{ fontSize: 10 }}>
                                            {k} : {v}
                                        </Tag>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Col>
            </Row>
        </Card>
    );
};

// ─── Page principale ────────────────────────────────────────────────────────

const RepriseSinistresDecl = () => {
    const [compagnies, setCompagnies]               = useState([]);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    const [fichiers, setFichiers]                   = useState([]);

    const [step, setStep]                           = useState(0);

    const [previews, setPreviews]                   = useState([]);
    const [previewLoading, setPreviewLoading]       = useState(false);

    const [rapports, setRapports]                   = useState([]);
    const [importLoading, setImportLoading]         = useState(false);
    const [progress, setProgress]                   = useState(0);
    const [currentFile, setCurrentFile]             = useState(null);

    const [forceModalVisible, setForceModalVisible] = useState(false);
    const [forceModalStep, setForceModalStep]       = useState(1);

    const abortRef = useRef(false);

    useEffect(() => {
        axiosInstance.get("/api/organisme/assurance")
            .then(res => setCompagnies(res.data))
            .catch(() => toast.error("Impossible de charger les compagnies"));
    }, []);

    const handleUploadChange = ({ fileList }) => setFichiers(fileList);

    // ── Étape 0 → 1 : Prévisualiser ─────────────────────────────────────────
    const lancerPreview = async () => {
        const valides = fichiers.filter(f => f.originFileObj);
        if (valides.length === 0) { toast.warn("Ajoutez au moins un fichier"); return; }

        setPreviewLoading(true);
        setPreviews([]);
        const resultats = [];

        for (const f of valides) {
            const formData = new FormData();
            formData.append("fichier", f.originFileObj);
            if (selectedCompagnie) formData.append("compagnie", selectedCompagnie.code);
            try {
                const res = await axiosMultiPartDataInstance.post(
                    "/api/migration/reprise-sinistre/preview", formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                resultats.push(res.data);
            } catch (err) {
                resultats.push({
                    fichier: f.name, ok: false,
                    messageErreur: err.response?.data?.message || err.message || "Erreur serveur",
                });
            }
        }

        setPreviews(resultats);
        setPreviewLoading(false);
        setStep(1);
        const nbErreurs = resultats.filter(r => !r.ok).length;
        if (nbErreurs > 0)
            toast.warn(`${nbErreurs} fichier(s) en erreur — vérifiez l'aperçu`);
        else if (resultats.some(r => r.totalLiesManquants > 0))
            toast.warn("Des polices sont manquantes — vérifiez l'aperçu avant d'importer");
        else
            toast.success("Aperçu généré — vérifiez les statistiques");
    };

    // ── Étape 1 → 2 → 3 : Importer (normal ou force) ────────────────────────
    const lancerImport = async (forceImport = false) => {
        const valides = fichiers.filter(f => f.originFileObj);
        if (valides.length === 0) return;

        setForceModalVisible(false);
        setForceModalStep(1);
        setImportLoading(true);
        abortRef.current = false;
        setRapports([]);
        setProgress(0);
        setStep(2);

        const resultats = [];
        for (let i = 0; i < valides.length; i++) {
            if (abortRef.current) break;
            const f = valides[i];
            setCurrentFile(f.name);
            const formData = new FormData();
            formData.append("fichier", f.originFileObj);
            if (selectedCompagnie) formData.append("compagnie", selectedCompagnie.code);
            if (forceImport) formData.append("forceImport", "true");
            try {
                const res = await axiosMultiPartDataInstance.post(
                    "/api/migration/reprise-sinistre", formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                resultats.push(res.data);
            } catch (err) {
                resultats.push({
                    fichier: f.name, ok: false,
                    messageErreurGlobal: err.response?.data?.message || err.message || "Erreur serveur",
                    totalParse: 0, succes: 0, dejaExistants: 0, forcesCreations: 0, erreurs: [],
                });
            }
            setProgress(Math.round(((i + 1) / valides.length) * 100));
        }

        setRapports(resultats);
        setImportLoading(false);
        setCurrentFile(null);
        setStep(3);
        const totalSucces      = resultats.reduce((s, r) => s + (r.succes || 0), 0);
        const totalForces      = resultats.reduce((s, r) => s + (r.forcesCreations || 0), 0);
        const totalErreurs     = resultats.reduce((s, r) => s + (r.erreurs?.length || 0), 0);
        if (forceImport && totalForces > 0)
            toast.warn(`Import force terminé — ${totalSucces} intégrés dont ${totalForces} force-créés, ${totalErreurs} rejetés`);
        else
            toast.success(`Import terminé — ${totalSucces} intégrés, ${totalErreurs} rejetés`);
    };

    const exporterRapport = () => {
        if (rapports.length === 0) return;
        const wb = XLSX.utils.book_new();
        const dateStr = new Date().toISOString().slice(0, 10);

        const resumeRows = [["Fichier", "Parsés", "Intégrés", "Force-créés", "Déjà existants", "Rejetés", "Durée (s)", "Compagnie"]];
        rapports.forEach(r => resumeRows.push([
            r.fichier, r.totalParse, r.succes, r.forcesCreations || 0, r.dejaExistants || 0,
            (r.erreurs || []).length,
            r.dureeMs != null ? (r.dureeMs / 1000).toFixed(1) : "—",
            selectedCompagnie?.code || r.codeCompagnie || "—",
        ]));
        const wsResume = XLSX.utils.aoa_to_sheet(resumeRows);
        wsResume["!cols"] = [{ wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, wsResume, "Résumé");

        const erreursRows = [["Fichier", "Ligne", "N° Sinistre", "N° Police", "Immatriculation", "Compagnie", "Assuré", "Cause du rejet"]];
        rapports.forEach(r => (r.erreurs || []).forEach(e => erreursRows.push([
            r.fichier, e.ligne ?? "", e.sinistre || "", e.police || "",
            e.immatriculation || "", e.compagnie || "", e.assure || "", e.erreur || "",
        ])));
        const wsErreurs = XLSX.utils.aoa_to_sheet(erreursRows);
        wsErreurs["!cols"] = [{ wch: 35 }, { wch: 8 }, { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 12 }, { wch: 20 }, { wch: 60 }];
        XLSX.utils.book_append_sheet(wb, wsErreurs, "Lignes rejetées");

        const compCode = (selectedCompagnie?.code || "").toUpperCase().replace(/[^A-Z0-9]/g, "_");
        XLSX.writeFile(wb, `REPRISE_SINISTRES_DECL_${compCode}_${dateStr}.xlsx`);
    };

    const resetAll = () => {
        setStep(0); setFichiers([]); setPreviews([]); setRapports([]);
        setProgress(0); setCurrentFile(null);
        setForceModalVisible(false); setForceModalStep(1);
    };

    // Agrégats
    const totalSinistresGlobal = previews.reduce((s, p) => s + (p.totalSinistres  || 0), 0);
    const totalTrouvesGlobal   = previews.reduce((s, p) => s + (p.totalLiesTrouves || 0), 0);
    const totalManquantsGlobal = previews.reduce((s, p) => s + (p.totalLiesManquants || 0), 0);
    const sommeEvalGlobal      = previews.reduce((s, p) => s + (p.sommeEvaluations || 0), 0);
    const totalParseGlobal     = rapports.reduce((s, r) => s + (r.totalParse  || 0), 0);
    const totalSuccesGlobal    = rapports.reduce((s, r) => s + (r.succes      || 0), 0);
    const totalForcesGlobal    = rapports.reduce((s, r) => s + (r.forcesCreations || 0), 0);
    const totalDejaGlobal      = rapports.reduce((s, r) => s + (r.dejaExistants || 0), 0);
    const totalRejetsGlobal    = rapports.reduce((s, r) => s + (r.erreurs?.length || 0), 0);

    return (
        <PageLayout currentMenu="REPRISE">
            <div className="container-xxl flex-grow-1 container-p-y">

                {/* En-tête */}
                <div className="mb-4">
                    <Title level={4} style={{ margin: 0, color: "#242166" }}>
                        <DatabaseOutlined className="me-2" />
                        Reprise — Sinistres Déclarés
                    </Title>
                    <Text type="secondary">
                        Formats acceptés : eFORCE (.xls) · BSD SANLAM (.xlsx) · Template FIDELIA (.xlsx)
                    </Text>
                </div>

                {/* Stepper */}
                <Card style={{ marginBottom: 24 }}>
                    <Steps current={step} size="small" items={[
                        { title: "Sélection",  icon: <FileExcelOutlined /> },
                        { title: "Aperçu",     icon: previewLoading ? <LoadingOutlined /> : <EyeOutlined /> },
                        { title: "Import",     icon: importLoading  ? <LoadingOutlined /> : <RocketOutlined /> },
                        { title: "Rapport",    icon: step === 3 ? <CheckCircleFilled style={{ color: "#52c41a" }} /> : <DatabaseOutlined /> },
                    ]} />
                </Card>

                {/* ── ÉTAPE 0 : Sélection ─────────────────────────────────── */}
                {step === 0 && (
                    <Card title="Paramètres d'import" style={{ borderTop: "3px solid #242166" }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={10}>
                                <div className="mb-2">
                                    <Text strong>Compagnie</Text>
                                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                        (optionnel pour eFORCE — la compagnie est lue depuis le fichier)
                                    </Text>
                                </div>
                                <Select
                                    placeholder="Sélectionner la compagnie (optionnel)"
                                    size="large" style={{ width: "100%" }}
                                    showSearch allowClear
                                    filterOption={(input, opt) =>
                                        opt.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(_, opt) => setSelectedCompagnie(opt?.data || null)}
                                    options={compagnies.map(c => ({ value: c.code, label: c.raisonSociale, data: c }))}
                                />
                                {!selectedCompagnie && (
                                    <Text style={{ fontSize: 11, color: "#1d4ed8" }}>
                                        Pour eFORCE, la compagnie est extraite du fichier.
                                        Pour BSD SANLAM et Template FIDELIA, sélectionnez la compagnie ici.
                                    </Text>
                                )}
                            </Col>
                            <Col xs={24} md={14}>
                                <div className="mb-2">
                                    <Text strong>Fichiers sinistres déclarés</Text>
                                    <Text type="secondary" className="ms-2">(plusieurs fichiers acceptés)</Text>
                                    <Tag color="default" className="ms-2" style={{ fontSize: 11 }}>Format détecté automatiquement</Tag>
                                </div>
                                <Dragger onChange={handleUploadChange} beforeUpload={() => false}
                                    accept=".xls,.xlsx" multiple fileList={fichiers}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined style={{ fontSize: 36, color: "#242166" }} />
                                    </p>
                                    <p className="ant-upload-text">Glissez les fichiers ici ou cliquez</p>
                                    <p className="ant-upload-hint text-muted">
                                        eFORCE SINISTRES_DECLARES.XLS · BSD SANLAM .xlsx · Template FIDELIA .xlsx
                                    </p>
                                </Dragger>
                            </Col>
                        </Row>

                        <Divider orientation="left" style={{ marginTop: 24 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Formats supportés</Text>
                        </Divider>
                        <Space wrap>
                            <Tag color="blue"   style={{ fontSize: 11 }}>eFORCE · SINISTRES_DECLARES.XLS · Compagnie dans le fichier</Tag>
                            <Tag color="orange" style={{ fontSize: 11 }}>BSD SANLAM · .xlsx · SINISTRE DECLARES · Nature X/M/C</Tag>
                            <Tag color="green"  style={{ fontSize: 11 }}>Template FIDELIA · .xlsx · feuille A · 14 colonnes</Tag>
                        </Space>

                        <div className="d-flex justify-content-end mt-4">
                            <Button type="primary" size="large" icon={<EyeOutlined />}
                                loading={previewLoading} onClick={lancerPreview}
                                style={{ backgroundColor: "#242166", minWidth: 200 }}
                                disabled={fichiers.filter(f => f.originFileObj).length === 0}>
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {/* ── ÉTAPE 1 : Aperçu ────────────────────────────────────── */}
                {step === 1 && previews.length > 0 && (
                    <>
                        <Card style={{ marginBottom: 16, borderTop: "3px solid #242166" }}>
                            <Row gutter={16} align="middle">
                                <Col xs={12} sm={6}>
                                    <Statistic title="Sinistres parsés" value={totalSinistresGlobal}
                                        valueStyle={{ color: "#242166", fontSize: 28 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Polices trouvées" value={totalTrouvesGlobal}
                                        valueStyle={{ color: "#52c41a", fontSize: 24 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Polices manquantes" value={totalManquantsGlobal}
                                        valueStyle={{ color: totalManquantsGlobal > 0 ? "#ff4d4f" : "#52c41a", fontSize: 24 }} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Total évaluations"
                                        formatter={() => (
                                            <Text strong style={{ fontSize: 16, color: "#166534" }}>
                                                {formatMontant(sommeEvalGlobal)}
                                            </Text>
                                        )}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {previews.map((preview, idx) => {
                            const lignes           = preview.lignes || [];
                            const nbAvertissements = lignes.filter(l => l._avertissement).length;
                            const nbImmatFallback  = lignes.filter(l => l._info).length;
                            return (
                                <div key={idx}>
                                    {!preview.ok && (
                                        <Alert type="error" showIcon message={`Erreur — ${preview.fichier}`}
                                            description={preview.messageErreur} style={{ marginBottom: 12 }} />
                                    )}
                                    {preview.ok && nbImmatFallback > 0 && (
                                        <Alert type="info" showIcon icon={<InfoCircleOutlined />}
                                            message={`${preview.fichier} — ${nbImmatFallback} sinistre(s) rattaché(s) via immatriculation`}
                                            style={{ marginBottom: 8 }} />
                                    )}
                                    {preview.ok && preview.totalLiesManquants > 0 && (
                                        <Alert type="warning" showIcon
                                            message={`${preview.fichier} — ${preview.totalLiesManquants} sinistre(s) sans contrat associé`}
                                            style={{ marginBottom: 8 }} />
                                    )}
                                    <Card title={<Space><EyeOutlined /><Text strong>{preview.fichier}</Text></Space>}
                                        style={{ marginBottom: 12 }}>
                                        <PreviewCard preview={preview} />
                                    </Card>
                                    {lignes.length > 0 && (
                                        <Card style={{ marginBottom: 16, padding: 0 }} bodyStyle={{ padding: 0 }}
                                            title={
                                                <Space>
                                                    <DatabaseOutlined />
                                                    <Text strong>Lignes</Text>
                                                    <Tag color="#242166">{preview.totalSinistres} sinistres</Tag>
                                                    {nbImmatFallback  > 0 && <Tag color="blue">{nbImmatFallback} via immat</Tag>}
                                                    {nbAvertissements > 0 && <Tag color="orange">{nbAvertissements} rejet(s)</Tag>}
                                                </Space>
                                            }
                                        >
                                            <Table
                                                dataSource={lignes.map((r, i) => ({ ...r, key: i }))}
                                                columns={previewDeclColumns}
                                                size="small"
                                                scroll={{ x: 1200, y: 440 }}
                                                pagination={{ defaultPageSize: 20, showSizeChanger: true, showQuickJumper: true }}
                                                rowClassName={r => r._avertissement ? "preview-row-warning" : r._info ? "preview-row-immat" : ""}
                                            />
                                        </Card>
                                    )}
                                </div>
                            );
                        })}

                        {/* Actions étape 1 */}
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <Button size="large" onClick={() => setStep(0)}>← Modifier</Button>
                            <Space wrap>
                                {totalManquantsGlobal > 0 && (
                                    <Tooltip title="Créer des enregistrements minimaux pour les lignes non résolues — double confirmation requise">
                                        <Button
                                            size="large"
                                            icon={<ThunderboltOutlined />}
                                            onClick={() => { setForceModalStep(1); setForceModalVisible(true); }}
                                            style={{ borderColor: "#faad14", color: "#faad14" }}
                                        >
                                            Importer en mode force ({totalManquantsGlobal} non résolu{totalManquantsGlobal > 1 ? "s" : ""})
                                        </Button>
                                    </Tooltip>
                                )}
                                <Button
                                    type="primary" size="large" icon={<RocketOutlined />}
                                    onClick={() => lancerImport(false)}
                                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", minWidth: 220 }}
                                    disabled={!previews.some(p => p.ok)}
                                >
                                    Confirmer et importer ({previews.filter(p => p.ok).length} fichier(s))
                                </Button>
                            </Space>
                        </div>
                    </>
                )}

                {/* ── ÉTAPE 2 : Import en cours ───────────────────────────── */}
                {step === 2 && (
                    <Card style={{ borderTop: "3px solid #52c41a" }}>
                        <div style={{ textAlign: "center", padding: "24px 0" }}>
                            <LoadingOutlined style={{ fontSize: 40, color: "#242166", marginBottom: 16 }} />
                            <div>
                                <Text strong style={{ fontSize: 16 }}>Import sinistres déclarés en cours…</Text>
                            </div>
                            <Text type="secondary">{currentFile}</Text>
                            <Progress percent={progress} strokeColor="#242166"
                                style={{ marginTop: 16, maxWidth: 500, margin: "16px auto 0" }} />
                        </div>
                    </Card>
                )}

                {/* ── ÉTAPE 3 : Rapport final ──────────────────────────────── */}
                {step === 3 && rapports.length > 0 && (
                    <>
                        <Card style={{ marginBottom: 16, borderTop: "3px solid #52c41a" }}>
                            <Row gutter={16}>
                                <Col xs={8} sm={4}>
                                    <Statistic title="Parsés" value={totalParseGlobal}
                                        valueStyle={{ color: "#1890ff" }} />
                                </Col>
                                <Col xs={8} sm={5}>
                                    <Statistic title="Intégrés" value={totalSuccesGlobal}
                                        valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleFilled />} />
                                </Col>
                                {totalForcesGlobal > 0 && (
                                    <Col xs={8} sm={5}>
                                        <Statistic title="Force-créés" value={totalForcesGlobal}
                                            valueStyle={{ color: "#faad14" }}
                                            prefix={<ThunderboltOutlined />} />
                                    </Col>
                                )}
                                <Col xs={8} sm={5}>
                                    <Statistic title="Déjà existants" value={totalDejaGlobal}
                                        valueStyle={{ color: "#faad14" }} />
                                </Col>
                                <Col xs={8} sm={5}>
                                    <Statistic title="Rejetés" value={totalRejetsGlobal}
                                        valueStyle={{ color: totalRejetsGlobal > 0 ? "#ff4d4f" : "#52c41a" }}
                                        prefix={totalRejetsGlobal > 0 ? <CloseCircleFilled /> : <CheckCircleFilled />} />
                                </Col>
                            </Row>
                            {totalForcesGlobal > 0 && (
                                <Alert type="warning" showIcon icon={<ThunderboltOutlined />}
                                    message={`${totalForcesGlobal} enregistrement(s) force-créés — marqués [FORCE-REPRISE] — à vérifier et compléter manuellement`}
                                    style={{ marginTop: 16 }} />
                            )}
                        </Card>

                        <Card
                            title={<Space><Text strong>Rapport d'import ({rapports.length} fichier(s))</Text></Space>}
                            extra={
                                <Space>
                                    <Button icon={<DownloadOutlined />} onClick={exporterRapport}>Exporter</Button>
                                    <Button onClick={resetAll}>Nouvelle reprise</Button>
                                </Space>
                            }
                        >
                            {rapports.map((rapport, idx) => (
                                <div key={idx} style={{ marginBottom: idx < rapports.length - 1 ? 24 : 0 }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <Space>
                                            <FileExcelOutlined style={{ color: "#217346" }} />
                                            <Text strong>{rapport.fichier}</Text>
                                            {rapport.forceImport && <Tag color="orange" icon={<ThunderboltOutlined />}>Force</Tag>}
                                        </Space>
                                        <Space>
                                            <Tag color="blue">{rapport.totalParse} parsés</Tag>
                                            <Tag color="green">{rapport.succes} intégrés</Tag>
                                            {(rapport.forcesCreations || 0) > 0 && (
                                                <Tag color="orange" icon={<ThunderboltOutlined />}>{rapport.forcesCreations} force-créés</Tag>
                                            )}
                                            {rapport.dejaExistants > 0 && <Tag color="orange">{rapport.dejaExistants} déjà existants</Tag>}
                                            {(rapport.erreurs || []).length > 0
                                                ? <Tag color="red">{rapport.erreurs.length} rejetés</Tag>
                                                : <Tag color="green">Aucun rejet</Tag>}
                                            {rapport.dureeMs && <Tag color="default">{(rapport.dureeMs / 1000).toFixed(1)}s</Tag>}
                                        </Space>
                                    </div>
                                    {!rapport.ok && rapport.messageErreurGlobal && (
                                        <Alert type="error" message="Échec global"
                                            description={rapport.messageErreurGlobal} showIcon style={{ marginBottom: 8 }} />
                                    )}
                                    {(rapport.erreurs || []).length > 0 && (
                                        <Table size="small" columns={erreurColumns}
                                            dataSource={(rapport.erreurs || []).map((e, i) => ({ ...e, key: i }))}
                                            pagination={{ defaultPageSize: 20, showSizeChanger: true, showQuickJumper: true,
                                                showTotal: (t, r) => `${r[0]}–${r[1]} sur ${t} rejet(s)` }}
                                            scroll={{ x: true }} />
                                    )}
                                    {idx < rapports.length - 1 && <Divider />}
                                </div>
                            ))}
                        </Card>
                    </>
                )}

            </div>

            {/* ── Modal double confirmation force ────────────────────────── */}
            <Modal
                open={forceModalVisible}
                onCancel={() => { setForceModalVisible(false); setForceModalStep(1); }}
                footer={null}
                width={520}
                closable={forceModalStep === 1}
                maskClosable={forceModalStep === 1}
                title={null}
            >
                {forceModalStep === 1 ? (
                    <>
                        <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
                            <ExclamationCircleOutlined style={{ fontSize: 52, color: "#faad14" }} />
                            <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                                Mode Force — Première confirmation
                            </Title>
                            <Text type="secondary">Lisez attentivement avant de continuer</Text>
                        </div>
                        <Alert type="warning" showIcon
                            message={`${totalManquantsGlobal} ligne(s) seront enregistrées avec données minimales`}
                            description={
                                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                    <li>Pour chaque ligne non résolue, PoolIns créera un contrat minimal (police + organisme) si nécessaire.</li>
                                    <li>Ces enregistrements sont marqués <Text code>[FORCE-REPRISE]</Text> et devront être <strong>vérifiés et complétés manuellement</strong>.</li>
                                    <li>Les lignes dont la police source est absente seront tout de même rejetées.</li>
                                </ul>
                            }
                            style={{ marginBottom: 20 }}
                        />
                        <div className="d-flex justify-content-end gap-2">
                            <Button onClick={() => setForceModalVisible(false)}>Annuler</Button>
                            <Button
                                style={{ backgroundColor: "#faad14", borderColor: "#faad14", color: "#fff" }}
                                onClick={() => setForceModalStep(2)}
                            >
                                Je comprends — Étape suivante →
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
                            <WarningOutlined style={{ fontSize: 52, color: "#ff4d4f" }} />
                            <Title level={4} style={{ marginTop: 12, marginBottom: 4, color: "#ff4d4f" }}>
                                Confirmation finale requise
                            </Title>
                        </div>
                        <Alert type="error" showIcon
                            message="Cette opération est irréversible"
                            description={
                                `Vous allez créer ${totalManquantsGlobal} enregistrement(s) avec données incomplètes, ` +
                                `marqués [FORCE-REPRISE]. Êtes-vous absolument certain de vouloir continuer ?`
                            }
                            style={{ marginBottom: 24 }}
                        />
                        <div className="d-flex justify-content-between">
                            <Button onClick={() => setForceModalStep(1)}>← Retour</Button>
                            <Button
                                danger type="primary"
                                icon={<ThunderboltOutlined />}
                                onClick={() => lancerImport(true)}
                            >
                                Oui — Lancer l'import en mode force
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            <style>{`
                .preview-row-warning td { background: #fff7ed !important; }
                .preview-row-warning:hover td { background: #fef3c7 !important; }
                .preview-row-immat td { background: #eff6ff !important; }
                .preview-row-immat:hover td { background: #dbeafe !important; }
            `}</style>
        </PageLayout>
    );
};

export default RepriseSinistresDecl;
